import { Link, useParams } from "react-router-dom";
import SEO from "../components/SEO";
import Header from "../components/Header";
import HeroSection from "../components/HeroSection";
import ServiceHighlights from "../components/ServiceHighlights";
import ServicesOverview from "../components/ServicesOverview";
import FAQ from "../components/FAQ";
import Footer from "../components/Footer";
import PartnerMarquee from "../components/PartnerMarquee";
import HelpButton from "../components/HelpButton";
import { generateLocalBusinessSchema } from "../lib/schema";
import { Check, Phone } from "lucide-react";
import { Reviews } from "../components/Reviews";

// Fixed areas that are common to all cities
const commonAreas = [
  "Centrum",
  "Noord",
  "Zuid",
  "Oost",
  "West",
  "Bedrijventerreinen",
  "Industriegebied",
  "Buitengebied",
];

// Additional areas specific to each city
const citySpecificAreas: { [key: string]: string[] } = {
  hoorn: [
    "Binnenstad",
    "Venenlaankwartier",
    "Hoorn-Noord",
    "Nieuwe Steen",
    "Risdam-Zuid",
    "Risdam-Noord",
    "Kersenboogerd",
    "Blokker",
    "Zwaag",
  ],
};

// Function to format city name for display
function formatCityName(name: string) {
  return name
    .replace(/-/g, " ")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function RegioDetail() {
  const { city } = useParams();
  const formattedCity = formatCityName(city || "");
  const normalizedCity = (city || "").toLowerCase();

  // Get areas for this city
  const areas = [
    ...commonAreas.map((area) => `${formattedCity} ${area}`),
    ...(citySpecificAreas[normalizedCity] || []),
  ];

  return (
    <div className="min-h-screen bg-gray-50 page-transition pt-[99px] md:pt-[133px]">
      <SEO
        title={`Elektricien in ${formattedCity} | Elektraaanleggen.nl`}
        description={`Uw elektricien in ${formattedCity}. Lokale expertise en snelle service.`}
        schema={generateLocalBusinessSchema(formattedCity)}
        image="https://elektraaanleggen.nl/images/banner.png"
      />

      <Header />
      <HeroSection
        title={`Elektricien in ${formattedCity}`}
        description={`Elektricien in ${formattedCity} en omgeving. Vraag vrijblijvend een offerte aan of bel direct! `}
        imageSrc="/images/mark.png"
      />

      <section className="mx-auto w-[calc(100%-2.5rem)] max-w-[1260px] mb-12 md:mb-16">
        <div className="mb-12">
          <h2 className="font-bold text-[24px] lg:text-[36px] text-[#17354A] mb-6">
            Werkgebied {formattedCity}
          </h2>
          <div className="flex flex-wrap gap-3">
            {areas.map((area, index) => (
              <span
                key={index}
                className="px-4 py-2 bg-[#F8F9FB] rounded-lg text-[#17354A]"
              >
                {area}
              </span>
            ))}
          </div>
        </div>

        <div className="grid items-center w-full gap-12 grid-cols-1 md:grid-cols-2 mb-16 md:mb-20 xl:mb-32">
          <div className="[&_p]:!text-[#17354A] [&_p]:text-base md:[&_p]:text-lg order-2 md:order-none">
            <h2 className="font-bold text-black leading-[1.25] text-[24px] lg:text-[36px] text-[#17354A] mb-4 md:mb-6">
              Op zoek naar een betrouwbare elektricien in {formattedCity}
            </h2>
            <p>
              Heeft u een elektricien in <strong>{formattedCity}</strong> nodig
              die snel kan handelen en ervaren is in alle elektra werkzaamheden?
              Dan bent u bij ons aan het juiste adres! Onze gediplomeerde en
              zegel gerechtigde elektriciens zijn gespecialiseerd in het
              aanleggen, uitbreiden en vervangen van groepenkasten,
              stopcontacten en stroompunten. Of het nu gaat om een nieuwe
              installatie, renovatie of het uitbreiden van uw meterkast, wij
              staan voor u klaar.
            </p>
            <br></br>
            <p>
              Met jarenlange ervaring in de elektrotechniek bieden wij
              betrouwbare en vakkundige oplossingen voor zowel particulieren als
              bedrijven. Van volledige elektra-installaties tot het vervangen
              van oude bedrading of het installeren van een 3-fase aansluiting,
              wij werken veilig, efficiënt en volgens de nieuwste NEN-normen.
            </p>
            <br></br>
            <ul className="space-y-3 mb-6">
              <li className="flex items-center gap-2">
                <Check className="w-5 h-5 text-[#F47B20]" />
                <span className="text-[#17354A]">
                  Snelle service, ook bij spoedklussen
                </span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-5 h-5 text-[#F47B20]" />
                <span className="text-[#17354A]">
                  Duidelijke prijzen en vrijblijvend advies vooraf
                </span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-5 h-5 text-[#F47B20]" />
                <span className="text-[#17354A]">
                  Vakkundige installatie met A-kwaliteit materialen
                </span>
              </li>
            </ul>
            <br></br>
            <p>
              Meer weten of direct een afspraak maken? Neem contact op met onze
              elektricien in {formattedCity}, wij helpen u graag verder!
            </p>

            <div className="h-9"></div>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/offerte"
                aria-label="Vraag een vrijblijvende offerte aan voor groepenkast vervanging"
                className="relative font-medium transition-all duration-150 leading-normal outline-none inline-flex border-2 border-transparent items-center justify-center rounded-full active:scale-[.98] disabled:cursor-not-allowed text-base px-5 h-12 leading-[48px] bg-[#F47B20] text-white hover:bg-[#F47B20]/80 focus:bg-[#F47B20]/80 focus-visible:border-crustaLight disabled:bg-[#F47B20]/40 w-full md:w-fit"
                style={{ minWidth: "216px" }}
              >
                <span className="flex items-center gap-1">Gratis offerte</span>
              </Link>

              <a
                href="tel:+31852129778"
                className="relative font-medium transition-all duration-150 leading-normal outline-none inline-flex border-2 border-[#17354A] items-center justify-center rounded-full active:scale-[.98] disabled:cursor-not-allowed text-base px-5 h-12 leading-[48px] text-[#17354A] hover:bg-[#17354A] hover:text-white focus:opacity-60 w-full md:w-fit"
                style={{ minWidth: "216px" }}
              >
                <span className="flex items-center gap-1">
                  <Phone className="w-4 h-4" />
                  Bel direct 085-2129778
                </span>
              </a>
            </div>
          </div>
          <div className="order-1 md:-order-1">
            <div className="relative">
              <img
                alt={`Elektricien in ${formattedCity}`}
                loading="lazy"
                className="rounded-lg w-full h-auto"
                src="/images/elektricien-12.jpg"
              />
            </div>
          </div>
        </div>

        <ServiceHighlights />

        <div className="grid items-center w-full gap-12 grid-cols-1 md:grid-cols-2 mb-16 md:mb-20 xl:mb-32">
          <div className="order-2 md:order-2">
            <div className="relative">
              <img
                alt={`Elektricien in ${formattedCity}`}
                loading="lazy"
                className="rounded-lg w-full h-auto border shadow-lg"
                src="/images/offerte3.jpg"
              />
            </div>
          </div>

          <div className="[&_p]:!text-[#17354A] [&_p]:text-base md:[&_p]:text-lg order-1 md:order-none">
            <h2 className="font-bold text-black leading-[1.25] text-[24px] lg:text-[36px] text-[#17354A] mb-4 md:mb-6">
              Transparante prijzen – Geen verrassingen, alleen zekerheid!
            </h2>
            <p>
              Bij Elektricien Elektraaanleggen, jouw elektricien in{" "}
              <strong>{formattedCity}</strong>, hanteren wij een helder en
              eerlijk prijsbeleid. Ons motto is: kwaliteit in stroom, eerlijk in
              prijs. Daarom voeren wij uitsluitend werkzaamheden uit waarvoor we
              vooraf een duidelijke prijsopgave kunnen geven. Dit betekent dat
              wij geen storingen verhelpen, omdat hierbij de kosten vooraf
              lastig te bepalen zijn. Zo voorkomen we verrassingen en bouwen we
              aan een vertrouwensrelatie met onze klanten, die ons graag
              aanbevelen bij familie en vrienden.
            </p>
            <br></br>
            <p>Duidelijke prijsopgave zonder verborgen kosten</p>
            <br></br>
            <ul className="space-y-3 mb-6">
              <li className="flex items-center gap-2">
                <Check className="w-5 h-5 text-[#F47B20]" />
                <span className="text-[#17354A]">
                  Kostenindicatie per telefoon – Snel en eenvoudig.
                </span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-5 h-5 text-[#F47B20]" />
                <span className="text-[#17354A]">
                  Offerte op aanvraag – Duidelijke tarieven vooraf.
                </span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-5 h-5 text-[#F47B20]" />
                <span className="text-[#17354A]">
                  Inspectie voor grote projecten – Nauwkeurige prijsopgave.
                </span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-5 h-5 text-[#F47B20]" />
                <span className="text-[#17354A]">
                  Efficiënte uitvoering – Volgens planning en afspraak.
                </span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-5 h-5 text-[#F47B20]" />
                <span className="text-[#17354A]">
                  Transparante betaling – 50% direct per pin na volledige
                  tevredenheid, de rest binnen 30 dagen via factuur.
                </span>
              </li>
            </ul>
            <br></br>
            <p>
              Bij Elektricien Elektraaanlegggen, dé elektricien in{" "}
              {formattedCity}, staan duidelijkheid en klanttevredenheid voorop.
              Wilt u een betrouwbare elektricien zonder verborgen kosten? Neem
              contact met ons op en ontvang een vrijblijvende offerte!
            </p>

            <div className="h-9"></div>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/offerte"
                aria-label="Vraag een vrijblijvende offerte aan voor groepenkast vervanging"
                className="relative font-medium transition-all duration-150 leading-normal outline-none inline-flex border-2 border-transparent items-center justify-center rounded-full active:scale-[.98] disabled:cursor-not-allowed text-base px-5 h-12 leading-[48px] bg-[#F47B20] text-white hover:bg-[#F47B20]/80 focus:bg-[#F47B20]/80 focus-visible:border-crustaLight disabled:bg-[#F47B20]/40 w-full md:w-fit"
                style={{ minWidth: "216px" }}
              >
                <span className="flex items-center gap-1">Gratis offerte</span>
              </Link>

              <a
                href="tel:+31852129778"
                className="relative font-medium transition-all duration-150 leading-normal outline-none inline-flex border-2 border-[#17354A] items-center justify-center rounded-full active:scale-[.98] disabled:cursor-not-allowed text-base px-5 h-12 leading-[48px] text-[#17354A] hover:bg-[#17354A] hover:text-white focus:opacity-60 w-full md:w-fit"
                style={{ minWidth: "216px" }}
              >
                <span className="flex items-center gap-1">
                  <Phone className="w-4 h-4" />
                  Bel direct 085-2129778
                </span>
              </a>
            </div>
          </div>
        </div>

        <div className="grid items-center w-full gap-12 grid-cols-1 md:grid-cols-2 mb-16 md:mb-20 xl:mb-32">
          <div className="[&_p]:!text-[#17354A] [&_p]:text-base md:[&_p]:text-lg order-2 md:order-none">
            <h2 className="font-bold text-black leading-[1.25] text-[24px] lg:text-[36px] text-[#17354A] mb-4 md:mb-6">
              Groepenkast vervangen in {formattedCity}
            </h2>
            <p>
              Een veilige en moderne groepenkast is essentieel voor een
              betrouwbare elektrische installatie. Verouderde groepenkasten
              vergroten het risico op storingen, kortsluiting en brandgevaar.
              Laat uw groepenkast professioneel vervangen door Elektricien
              Elektraaanleggen, uw ervaren elektricien in{" "}
              <strong>{formattedCity}</strong>. Wij werken volgens de nieuwste
              NEN 1010-normen en zorgen voor een veilige en toekomstbestendige
              installatie.
            </p>
            <br></br>
            <p>Wanneer moet uw groepenkast vervangen worden?</p>
            <br></br>
            <ul className="space-y-3 mb-6">
              <li className="flex items-center gap-2">
                <Check className="w-5 h-5 text-[#F47B20]" />
                <span className="text-[#17354A]">
                  Groepenkast ouder dan 25 jaar – Vaak niet meer veilig en
                  voldoet niet aan de huidige eisen.
                </span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-5 h-5 text-[#F47B20]" />
                <span className="text-[#17354A]">
                  Geen aardlekschakelaars – Voor extra veiligheid is een
                  aardlekschakelaar wettelijk verplicht.
                </span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-5 h-5 text-[#F47B20]" />
                <span className="text-[#17354A]">
                  Regelmatige stroomstoringen of kortsluiting – Een teken dat uw
                  groepenkast overbelast of verouderd is.
                </span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-5 h-5 text-[#F47B20]" />
                <span className="text-[#17354A]">
                  Uitbreiding van elektra – Heeft u zonnepanelen, een
                  warmtepomp, inductiekookplaat of laadpaal? Dan is een upgrade
                  noodzakelijk.
                </span>
              </li>
            </ul>
            <br></br>
            <p>
              Meer weten? Klik op de knop hieronder en ontdek alles over
              groepenkast vervangen in <strong>{formattedCity}</strong>!
            </p>

            <div className="h-9"></div>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/groepenkast-vervangen"
                aria-label="Vraag een vrijblijvende offerte aan voor groepenkast vervanging"
                className="relative font-medium transition-all duration-150 leading-normal outline-none inline-flex border-2 border-transparent items-center justify-center rounded-full active:scale-[.98] disabled:cursor-not-allowed text-base px-5 h-12 leading-[48px] bg-[#F47B20] text-white hover:bg-[#F47B20]/80 focus:bg-[#F47B20]/80 focus-visible:border-crustaLight disabled:bg-[#F47B20]/40 w-full md:w-fit"
                style={{ minWidth: "216px" }}
              >
                <span className="flex items-center gap-1">
                  Meer informatie over groepenkast vervangen
                </span>
              </Link>

              <a
                href="tel:+31852129778"
                className="relative font-medium transition-all duration-150 leading-normal outline-none inline-flex border-2 border-[#17354A] items-center justify-center rounded-full active:scale-[.98] disabled:cursor-not-allowed text-base px-5 h-12 leading-[48px] text-[#17354A] hover:bg-[#17354A] hover:text-white focus:opacity-60 w-full md:w-fit"
                style={{ minWidth: "216px" }}
              >
                <span className="flex items-center gap-1">
                  <Phone className="w-4 h-4" />
                  Bel direct 085-2129778
                </span>
              </a>
            </div>
          </div>
          <div className="order-1 md:-order-1">
            <div className="relative">
              <img
                alt={`Elektricien in ${formattedCity}`}
                loading="lazy"
                className="rounded-lg w-full h-auto"
                src="/images/ABB.png"
              />
            </div>
          </div>
        </div>

        <div className="grid items-center w-full gap-12 grid-cols-1 md:grid-cols-2 mb-16 md:mb-20 xl:mb-32">
          <div className="order-2 md:order-2">
            <div className="relative">
              <img
                alt={`Elektricien in ${formattedCity}`}
                loading="lazy"
                className="rounded-lg w-full h-auto sticky top-0"
                src="/images/perilex-kookplaat.png"
              />
            </div>
          </div>

          <div className="[&_p]:!text-[#17354A] [&_p]:text-base md:[&_p]:text-lg order-1 md:order-none">
            <h2 className="font-bold text-black leading-[1.25] text-[24px] lg:text-[36px] text-[#17354A] mb-4 md:mb-6">
              Kookgroep en Perilex Aansluiting in {formattedCity}
            </h2>
            <p>
              Heeft u een inductiekookplaat die u wilt laten aansluiten in uw
              nieuwe of bestaande keuken? Dan is een kookgroep en
              Perilex-aansluiting noodzakelijk. Elektricien Elektraaanleggen is
              uw betrouwbare partner in <strong>{formattedCity}</strong> voor
              een veilige en storingsvrije installatie, volledig volgens de NEN
              1010-normen.
            </p>
            <br></br>
            <p>
              {" "}
              Met jarenlange ervaring in het aanleggen van elektrische
              aansluitingen in keukens, zorgen wij voor een perfecte
              installatie. Of u nu een 2-fase of 3-fase inductiekookplaat heeft,
              wij geven u het juiste advies en installeren de aansluiting
              nauwkeurig. Daarnaast plaatsen we stopcontacten op de juiste plek,
              zelfs als ze moeten worden ingefreesd voor een strak
              eindresultaat.
            </p>
            <br></br>
            <p>
              Waarom kiezen voor Elektricien Elektraaanleggen in{" "}
              <strong>{formattedCity}</strong>{" "}
            </p>
            <br></br>
            <ul className="space-y-3 mb-6">
              <li className="flex items-center gap-2">
                <Check className="w-5 h-5 text-[#F47B20]" />
                <span className="text-[#17354A]">
                  Professionele installatie – Voorkomt storingen en
                  overbelasting.
                </span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-5 h-5 text-[#F47B20]" />
                <span className="text-[#17354A]">
                  2-fase of 3-fase aansluiting – Advies op maat voor uw keuken.
                </span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-5 h-5 text-[#F47B20]" />
                <span className="text-[#17354A]">
                  Veilig en volgens NEN-normen – Voldoet aan alle wettelijke
                  eisen.
                </span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-5 h-5 text-[#F47B20]" />
                <span className="text-[#17354A]">
                  Binnen één werkdag aangesloten – Snel en zonder gedoe.
                </span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-5 h-5 text-[#F47B20]" />
                <span className="text-[#17354A]">
                  Gecertificeerde elektricien – Geregistreerd bij Techniek
                  Nederland en InstallQ.
                </span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-5 h-5 text-[#F47B20]" />
                <span className="text-[#17354A]">
                  Stopcontacten op de juiste plek – Ook indien nodig ingefreesd
                  voor een strak resultaat.
                </span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-5 h-5 text-[#F47B20]" />
                <span className="text-[#17354A]">
                  Vrijblijvend advies & offerte – Stuur ons foto’s van uw
                  keukentekening en ontvang dezelfde dag nog een offerte.
                </span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-5 h-5 text-[#F47B20]" />
                <span className="text-[#17354A]">
                  Garantie op alle installaties – Zorgeloos genieten van uw
                  nieuwe kookplaat.
                </span>
              </li>
            </ul>
            <br></br>
            <p>
              Direct een kookgroep laten plaatsen? Klik hieronder en vraag een
              vrijblijvende offerte aan!
            </p>

            <div className="h-9"></div>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/offerte"
                aria-label="Vraag een vrijblijvende offerte aan voor groepenkast vervanging"
                className="relative font-medium transition-all duration-150 leading-normal outline-none inline-flex border-2 border-transparent items-center justify-center rounded-full active:scale-[.98] disabled:cursor-not-allowed text-base px-5 h-12 leading-[48px] bg-[#F47B20] text-white hover:bg-[#F47B20]/80 focus:bg-[#F47B20]/80 focus-visible:border-crustaLight disabled:bg-[#F47B20]/40 w-full md:w-fit"
                style={{ minWidth: "216px" }}
              >
                <span className="flex items-center gap-1">Gratis offerte</span>
              </Link>

              <a
                href="tel:+31852129778"
                className="relative font-medium transition-all duration-150 leading-normal outline-none inline-flex border-2 border-[#17354A] items-center justify-center rounded-full active:scale-[.98] disabled:cursor-not-allowed text-base px-5 h-12 leading-[48px] text-[#17354A] hover:bg-[#17354A] hover:text-white focus:opacity-60 w-full md:w-fit"
                style={{ minWidth: "216px" }}
              >
                <span className="flex items-center gap-1">
                  <Phone className="w-4 h-4" />
                  Bel direct 085-2129778
                </span>
              </a>
            </div>
          </div>
        </div>

        <div className="grid items-center w-full gap-12 grid-cols-1 md:grid-cols-2 mb-16 md:mb-20 xl:mb-32">
          <div className="[&_p]:!text-[#17354A] [&_p]:text-base md:[&_p]:text-lg order-2 md:order-none">
            <h2 className="font-bold text-black leading-[1.25] text-[24px] lg:text-[36px] text-[#17354A] mb-4 md:mb-6">
              Elektra & renovaties – van prijsindicatie tot planning
            </h2>
            <p>
              Bij Elektricien Elektraaanleggen begrijpen we dat elke renovatie
              uniek is. Of je nu een kleine verbouwing uitvoert of een volledige
              woningrenovatie, wij zorgen ervoor dat jouw elektra veilig,
              efficiënt en volgens de nieuwste normen wordt aangelegd. Als
              ervaren elektricien in <strong>{formattedCity}</strong> helpen we
              je graag bij al jouw elektra-werkzaamheden.
            </p>
            <br></br>
            <p>Direct een prijsindicatie bij kleinere renovaties</p>
            <br></br>
            <p>
              Voor kleinere renovaties, zoals het verplaatsen van stopcontacten,
              uitbreiden van een groep of het aansluiten van een kookgroep,
              kunnen wij vaak telefonisch al een schatting geven op basis van
              jouw wensen. Stuur ons eenvoudig de details van je project, zoals
              een keukentekening of plattegrond, en wij voorzien je snel van een
              indicatieve prijs.
            </p>
            <br></br>
            <p>Persoonlijke inspectie bij een volledige woningrenovatie</p>
            <br></br>
            <p>
              Gaat het om een complete woningrenovatie of een grote verbouwing?
              Dan komt onze elektricien in <strong>{formattedCity}</strong>{" "}
              graag persoonlijk langs voor een grondige inspectie. Tijdens dit
              bezoek:
            </p>
            <ul className="space-y-3 mb-6">
              <li className="flex items-center gap-2">
                <Check className="w-5 h-5 text-[#F47B20]" />
                <span className="text-[#17354A]">
                  Kijken we samen naar jouw wensen en plannen
                </span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-5 h-5 text-[#F47B20]" />
                <span className="text-[#17354A]">
                  Beoordelen we de huidige elektrische installatie
                </span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-5 h-5 text-[#F47B20]" />
                <span className="text-[#17354A]">
                  Bepalen we welke aanpassingen nodig zijn
                </span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-5 h-5 text-[#F47B20]" />
                <span className="text-[#17354A]">
                  Geven we een gedetailleerde prijsindicatie
                </span>
              </li>

              <li className="flex items-center gap-2">
                <Check className="w-5 h-5 text-[#F47B20]" />
                <span className="text-[#17354A]">
                  Stellen we een realistisch tijdsbestek op voor de uitvoering
                </span>
              </li>
            </ul>
            <br></br>
            <p>
              Met een duidelijk plan en een gestructureerde aanpak zorgt
              Elektricien Elektraaanleggen, jouw elektricien in{" "}
              <strong>{formattedCity}</strong>, ervoor dat de elektra in jouw
              woning volledig klaar is voor de toekomst.
            </p>

            <div className="h-9"></div>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/offerte"
                aria-label="Vraag een vrijblijvende offerte aan voor groepenkast vervanging"
                className="relative font-medium transition-all duration-150 leading-normal outline-none inline-flex border-2 border-transparent items-center justify-center rounded-full active:scale-[.98] disabled:cursor-not-allowed text-base px-5 h-12 leading-[48px] bg-[#F47B20] text-white hover:bg-[#F47B20]/80 focus:bg-[#F47B20]/80 focus-visible:border-crustaLight disabled:bg-[#F47B20]/40 w-full md:w-fit"
                style={{ minWidth: "216px" }}
              >
                <span className="flex items-center gap-1">Gratis offerte</span>
              </Link>

              <a
                href="tel:+31852129778"
                className="relative font-medium transition-all duration-150 leading-normal outline-none inline-flex border-2 border-[#17354A] items-center justify-center rounded-full active:scale-[.98] disabled:cursor-not-allowed text-base px-5 h-12 leading-[48px] text-[#17354A] hover:bg-[#17354A] hover:text-white focus:opacity-60 w-full md:w-fit"
                style={{ minWidth: "216px" }}
              >
                <span className="flex items-center gap-1">
                  <Phone className="w-4 h-4" />
                  Bel direct 085-2129778
                </span>
              </a>
            </div>
          </div>
          <div className="order-1 md:-order-1">
            <div className="relative">
              <img
                alt={`Elektricien in ${formattedCity}`}
                loading="lazy"
                className="rounded-lg w-full h-auto"
                src="/images/renovatie-thumb.jpg"
              />
            </div>
          </div>
        </div>

        <div className="grid items-center w-full gap-12 grid-cols-1 md:grid-cols-2 mb-16 md:mb-20 xl:mb-32">
          <div className="[&_p]:!text-[#17354A] [&_p]:text-base md:[&_p]:text-lg order-1 md:order-none">
            <h2 className="font-bold text-black leading-[1.25] text-[24px] lg:text-[36px] text-[#17354A] mb-4 md:mb-6">
              3-Fasen Aansluiting – Zorgeloos en Veilig Geregeld!
            </h2>
            <p>
              Wil je je aansluitvermogen uitbreiden naar 3 fasen? Dit is
              essentieel als je een elektrische auto, inductiekookplaat of
              warmtepomp gebruikt en een stabiele, krachtige stroomvoorziening
              nodig hebt. De overstap naar een 3-fase groepenkast zorgt ervoor
              dat je meer elektrische apparaten tegelijkertijd kunt gebruiken
              zonder overbelasting.
            </p>
            <br></br>
            <p>
              Wij zijn jouw lokale elektricien in{" "}
              <strong>{formattedCity}</strong> en staan klaar om dit vakkundig
              voor je te regelen!
            </p>
            <br></br>
            <p>Hoe werkt de overstap naar een 3-fasen aansluiting?</p>

            <ul className="space-y-3 mb-6">
              <li className="flex items-center gap-2">
                <Check className="w-5 h-5 text-[#F47B20]" />
                <span className="text-[#17354A]">
                  <strong>Stap 1:</strong> Voorbereiding groepenkast – Wij
                  bereiden de groepenkast voor op de 3-fasen aansluiting, zodat
                  alles klaar is voor de netbeheerder.
                </span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-5 h-5 text-[#F47B20]" />
                <span className="text-[#17354A]">
                  <strong>Stap 2:</strong> Netbeheerder voltooit de aansluiting
                  – De netbeheerder rondt de installatie af door alle 3 fasen
                  aan te sluiten op het hoofdnetwerk.
                </span>
              </li>
            </ul>

            <br></br>

            <p>
              Snelle en vakkundige installatie door gecertificeerde elektriciens
              in <strong>{formattedCity}</strong>.
            </p>

            <ul className="space-y-3 mb-6">
              <li className="flex items-center gap-2">
                <Check className="w-5 h-5 text-[#F47B20]" />
                <span className="text-[#17354A]">
                  Geregistreerd bij Techniek Nederland en InstallQ – Veilig en
                  professioneel geregeld.
                </span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-5 h-5 text-[#F47B20]" />
                <span className="text-[#17354A]">
                  Direct klaar voor zwaardere stroomverbruikers zoals
                  inductiekookplaten en laadpalen.
                </span>
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-5 h-5 text-[#F47B20]" />
                <span className="text-[#17354A]">Binnen één dag geregeld!</span>
              </li>
            </ul>

            <div className="h-9"></div>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/offerte"
                aria-label="Vraag een vrijblijvende offerte aan voor groepenkast vervanging"
                className="relative font-medium transition-all duration-150 leading-normal outline-none inline-flex border-2 border-transparent items-center justify-center rounded-full active:scale-[.98] disabled:cursor-not-allowed text-base px-5 h-12 leading-[48px] bg-[#F47B20] text-white hover:bg-[#F47B20]/80 focus:bg-[#F47B20]/80 focus-visible:border-crustaLight disabled:bg-[#F47B20]/40 w-full md:w-fit"
                style={{ minWidth: "216px" }}
              >
                <span className="flex items-center gap-1">Gratis offerte</span>
              </Link>

              <a
                href="tel:+31852129778"
                className="relative font-medium transition-all duration-150 leading-normal outline-none inline-flex border-2 border-[#17354A] items-center justify-center rounded-full active:scale-[.98] disabled:cursor-not-allowed text-base px-5 h-12 leading-[48px] text-[#17354A] hover:bg-[#17354A] hover:text-white focus:opacity-60 w-full md:w-fit"
                style={{ minWidth: "216px" }}
              >
                <span className="flex items-center gap-1">
                  <Phone className="w-4 h-4" />
                  Bel direct 085-2129778
                </span>
              </a>
            </div>
          </div>
          <div className="order-1 md:order-2">
            <div className="relative">
              <img
                alt={`Elektricien in ${formattedCity}`}
                loading="lazy"
                className="rounded-lg w-full h-auto"
                src="/images/mt2.png"
              />
            </div>
          </div>
        </div>

        <div className="grid items-center w-full gap-12 grid-cols-1 md:grid-cols-2 mb-16 md:mb-20 xl:mb-32">
          <div className="[&_p]:!text-[#17354A] [&_p]:text-base md:[&_p]:text-lg order-2 md:order-none">
            <h2 className="font-bold text-black leading-[1.25] text-[24px] lg:text-[36px] text-[#17354A] mb-4 md:mb-6">
              Lokale elektricien uit <strong>{formattedCity}</strong>
            </h2>
            <p>
              Wanneer u zoekt naar een elektricien in{" "}
              <strong>{formattedCity}</strong>, wilt u natuurlijk het liefst een
              lokale elektricien. Bij Elektricien Elektraaanleggen zijn wij al
              jaren een vertrouwde naam in de regio en behoren wij tot de beste
              elektriciens in <strong>{formattedCity}</strong> en omstreken.
            </p>
            <br></br>
            <p>Direct een prijsindicatie bij kleinere renovaties</p>
            <br></br>
            <p>
              Wij voorzien in alle elektrische behoeften, of het nu gaat om het
              onderhouden van bestaande systemen in oudere panden of het
              professioneel aanleggen van een nieuwe meterkast in een
              nieuwbouwwoning.
            </p>
            <br></br>
            <p>
              Kortom, heeft u een ervaren en betrouwbare professional nodig? Dan
              bent u bij onze elektromonteur uit{" "}
              <strong>{formattedCity}</strong> aan het juiste adres. Wij staan
              garant voor een scherpe offerte, duidelijke communicatie en
              vakmanschap.
            </p>
            <div className="h-9"></div>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/offerte"
                aria-label="Vraag een vrijblijvende offerte aan voor groepenkast vervanging"
                className="relative font-medium transition-all duration-150 leading-normal outline-none inline-flex border-2 border-transparent items-center justify-center rounded-full active:scale-[.98] disabled:cursor-not-allowed text-base px-5 h-12 leading-[48px] bg-[#F47B20] text-white hover:bg-[#F47B20]/80 focus:bg-[#F47B20]/80 focus-visible:border-crustaLight disabled:bg-[#F47B20]/40 w-full md:w-fit"
                style={{ minWidth: "216px" }}
              >
                <span className="flex items-center gap-1">Gratis offerte</span>
              </Link>

              <a
                href="tel:+31852129778"
                className="relative font-medium transition-all duration-150 leading-normal outline-none inline-flex border-2 border-[#17354A] items-center justify-center rounded-full active:scale-[.98] disabled:cursor-not-allowed text-base px-5 h-12 leading-[48px] text-[#17354A] hover:bg-[#17354A] hover:text-white focus:opacity-60 w-full md:w-fit"
                style={{ minWidth: "216px" }}
              >
                <span className="flex items-center gap-1">
                  <Phone className="w-4 h-4" />
                  Bel direct 085-2129778
                </span>
              </a>
            </div>
          </div>
          <div className="order-1 md:-order-1">
            <div className="relative">
              <img
                alt={`Elektricien in ${formattedCity}`}
                loading="lazy"
                className="rounded-lg w-full h-auto"
                src="/images/inspect.jpg"
              />
            </div>
          </div>
        </div>

        {/* Services Grid */}
      </section>

      <ServicesOverview />
      <Reviews />
      <FAQ />

      <PartnerMarquee />

      <div className="w-full cursor-pointer bg-[#2FB6D2] transition-opacity duration-150 hover:opacity-80">
        <Link
          to="/offerte"
          aria-label="Vraag een vrijblijvende offerte aan voor groepenkast vervanging"
        >
          <section className="mx-auto w-[calc(100%-2.5rem)] max-w-[1260px] text-center py-6 justify-between items-center">
            <div
              data-variant="h2"
              className="font-bold leading-[1.25] text-[28px] lg:text-[36px] self-center text-white mb-0"
            >
              Gratis offerte
            </div>
          </section>
        </Link>
      </div>

      <HelpButton />
      <Footer />
    </div>
  );
}

export default RegioDetail;
